/**
 * The member greeting the customer display shows (and, where the register's
 * Voice Greeting switch is on, speaks) when a rewards member enters their
 * phone number. Stores set their own per location on the POS Configuration
 * page; blank means this default. Mirrors customer-display's
 * lib/greeting_template.dart — keep the two in step.
 */
export const DEFAULT_CUSTOMER_DISPLAY_GREETING =
  'Hi {firstName}!\n' +
  'Thank you for visiting {storeName}!\n' +
  'We hope you enjoyed your shopping experience.\n' +
  'Have a great day!';

/** Matches the API's cap: about one screen, ~5 seconds spoken. */
export const CUSTOMER_DISPLAY_GREETING_MAX = 150;

export const GREETING_PLACEHOLDERS = ['{firstName}', '{storeName}'] as const;

const placeholder = /\{\s*(firstName|storeName)\s*\}/gi;
const firstSentence = /^.*?[.!?](?=\s|$)/;

/**
 * Fill a greeting for one member and split it the way the display does: the
 * first sentence is the headline, the rest the lines beneath it. Line breaks
 * are kept (blank lines and doubled spaces are dropped), and a first line with
 * no sentence end is the headline whole. A missing first name reads "there"
 * ("Hi there!"), a missing store name "our store".
 */
export function renderCustomerDisplayGreeting(
  template: string,
  { firstName, storeName }: { firstName: string; storeName: string },
): { title: string; message: string } {
  const source = template.trim() === '' ? DEFAULT_CUSTOMER_DISPLAY_GREETING : template;
  const text = source
    .replace(placeholder, (_, key: string) =>
      key.toLowerCase() === 'firstname' ? firstName || 'there' : storeName || 'our store',
    )
    .split(/[\r\n]+/)
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter((line) => line !== '')
    .join('\n');
  const headline = firstSentence.exec(text);
  if (headline) return { title: headline[0].trim(), message: text.slice(headline[0].length).trim() };
  const firstBreak = text.indexOf('\n');
  if (firstBreak < 0) return { title: text, message: '' };
  return { title: text.slice(0, firstBreak), message: text.slice(firstBreak + 1) };
}
